import React, { useState, useEffect } from 'react';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Container,
  Section,
  Title,
  ContactInfo,
  TwoColumnLayout,
  FormColumn,
  InfoColumn,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  SubmitButton,
  SubmitButtonDisabled,
  StatusMessage,
  InfoItem,
  InfoTitle,
  InfoText,
  InfoLink,
} from './Contact.styled';

// Production API endpoint
const API_ENDPOINT = '/api/send-email';

const Contact = () => {
  const navigate = useNavigate();
  // Scroll to top instantly when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    requirement: '',
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({
      submitting: true,
      submitted: false,
      error: false,
      message: '',
    });

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          requirement: formData.requirement,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus({
          submitting: false,
          submitted: true,
          error: false,
          message: '',
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          requirement: '',
        });
        // Navigate to thank you page
        navigate('/thank-you');
      } else {
        throw new Error(data.error || 'Something went wrong');
      }
    } catch (error) {
      setStatus({
        submitting: false,
        submitted: false,
        error: true,
        message: error.message || 'Failed to send message. Please try again.',
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <PageContainer>
      <Header />
      <Container
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Section variants={itemVariants}>
          <Title>CONTACT US</Title>
          <ContactInfo variants={itemVariants}>
            Let's join hands and hold things together, the right way.
          </ContactInfo>
        </Section>

        <Section variants={itemVariants}>
          <TwoColumnLayout>
            <FormColumn>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 1234567890"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    placeholder="Your location"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="requirement">Your Requirement *</Label>
                  <TextArea
                    id="requirement"
                    name="requirement"
                    value={formData.requirement}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Describe your requirement..."
                  />
                </FormGroup>

                {status.submitting ? (
                  <SubmitButtonDisabled type="submit" disabled>
                    Sending...
                  </SubmitButtonDisabled>
                ) : (
                  <SubmitButton type="submit">Send Message</SubmitButton>
                )}
                {status.message && (
                  <StatusMessage
                    success={status.submitted}
                    error={status.error}
                  >
                    {status.message}
                  </StatusMessage>
                )}
              </Form>
            </FormColumn>

            <InfoColumn>
              <InfoItem>
                <InfoTitle>Address</InfoTitle>
                <InfoText>
                  Swan Pipe Support Pvt. Ltd.
                  <br />
                  No. 20, Chockalinga Nagar 5th Street,
                  <br />
                  Koodal Nagar, Madurai,
                  <br />
                  Tamil Nadu 625018,
                  <br />
                  India
                </InfoText>
              </InfoItem>

              <InfoItem>
                <InfoTitle>Email</InfoTitle>
                <InfoLink href="mailto:info@swanclamps.com">
                  info@swanclamps.com
                </InfoLink>
              </InfoItem>

              <InfoItem>
                <InfoTitle>Phone</InfoTitle>
                <InfoLink href="tel:+919600342059">+91 9600342059</InfoLink>
                <InfoLink href="tel:+917305940534">+91 7305940534</InfoLink>
                <InfoLink href="tel:+917305940514">+91 7305940514</InfoLink>
              </InfoItem>

              <InfoItem>
                <InfoTitle>Business Hours</InfoTitle>
                <InfoText>
                  Monday - Saturday: 10:00 AM - 7:00 PM
                  <br />
                  Sunday: Closed
                </InfoText>
              </InfoItem>
            </InfoColumn>
          </TwoColumnLayout>
        </Section>
      </Container>
      <Footer />
    </PageContainer>
  );
};

export default Contact;
